import { useEffect } from 'react';
import type { CardMetadata } from '../types/catalog';
import { isUnit } from '../types/catalog';
import { CardImage } from './CardImage';
import { AttackBadge, EnergyBadge, KindBadge, TypeBadge } from './Badges';
import { CARD_TYPE_LABEL, GENDER_LABEL, describeTargetSpec } from '../i18n/labels';

interface CardModalProps {
  card: CardMetadata;
  onClose: () => void;
}

/** Detalhe de uma carta em modal. Fecha com Esc ou clique fora. */
export function CardModal({ card, onClose }: CardModalProps): React.JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={card.name}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-art">
          <CardImage artUrl={card.artUrl} alt={card.name} />
          <button type="button" className="modal-close" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>
        <div className="modal-content">
          <h2>{card.name}</h2>
          <div className="modal-row">
            <TypeBadge type={card.type} />
            <EnergyBadge cost={card.cost} />
            {isUnit(card) && <AttackBadge attack={card.baseAttack} />}
            {isUnit(card) && card.kinds.map((kind) => <KindBadge key={kind} kind={kind} />)}
          </div>

          {card.description ? (
            <p className="modal-desc">{card.description}</p>
          ) : (
            <p className="modal-desc" style={{ color: 'var(--text-faint)' }}>
              Sem texto de regras.
            </p>
          )}

          <dl className="modal-meta">
            <dt>Tipo</dt>
            <dd>{CARD_TYPE_LABEL[card.type]}</dd>

            <dt>Custo</dt>
            <dd>{card.cost} de energia</dd>

            {isUnit(card) && (
              <>
                <dt>Ataque</dt>
                <dd>{card.baseAttack}</dd>
              </>
            )}

            {isUnit(card) && card.gender && (
              <>
                <dt>Gênero</dt>
                <dd>{GENDER_LABEL[card.gender]}</dd>
              </>
            )}

            {card.type === 'spell' && (
              <>
                <dt>Alvo</dt>
                <dd>{describeTargetSpec(card.targetSpec)}</dd>
              </>
            )}

            <dt>ID</dt>
            <dd>
              <code>{card.cardId}</code>
            </dd>
          </dl>
        </div>
      </div>
    </div>
  );
}
